import styled from 'styled-components';
import PropTypes from 'prop-types';
import { ButtonIcon, ButtonIconBg, BackButtonIconBg } from '@comeon/ui';
import Icon, { ICON_CLOSE, ICON_ARROW_LEFT } from '~/components/icon/index.js';
import { getZindexByType } from '~/utils/style-utils.ts';
import SimpleSidebarHeader from './simple-sidebar-header.js';
import { useCloseCurrentContext } from '~/hooks/use-close-context.js';

const StyledDefaultHeader = styled(SimpleSidebarHeader)`
	height: var(--sidebarHeaderHeight);
	white-space: nowrap;
	z-index: ${props => (props.zIndex ? `${props.zIndex}` : `${getZindexByType('header')}`)};
`;

export const LeftSideIconType = Object.freeze({
	NONE: 'NONE',
	CLOSE_ICON: 'CLOSE_ICON',
	CLOSE_ICON_BG: 'CLOSE_ICON_BG',
	BACK_ICON: 'BACK_ICON',
	BACK_ICON_BG: 'BACK_ICON_BG'
});

const LeftSideIcon = ({ onClick, type }) => {
	if (!type || type === LeftSideIconType.NONE) {
		return null;
	}

	if (type === LeftSideIconType.CLOSE_ICON) {
		return (
			<ButtonIcon onClick={onClick} dataAt="close-button-general" data-testid="sidebar-header__close-icon-button">
				<Icon type={ICON_CLOSE} />
			</ButtonIcon>
		);
	}

	if (type === LeftSideIconType.CLOSE_ICON_BG) {
		return (
			<ButtonIconBg
				onClick={onClick}
				dataAt="close-button-general"
				data-testid="sidebar-header__close-icon-button"
			>
				<Icon type={ICON_CLOSE} />
			</ButtonIconBg>
		);
	}

	if (type === LeftSideIconType.BACK_ICON) {
		return (
			<ButtonIcon onClick={onClick} data-testid="sidebar-header__back-icon-button" dataAt="back-button-general">
				<Icon type={ICON_ARROW_LEFT} data-testid="sidebar-header__back-icon-button" />
			</ButtonIcon>
		);
	}

	if (type === LeftSideIconType.BACK_ICON_BG) {
		return (
			<BackButtonIconBg
				onClick={onClick}
				data-testid="sidebar-header__back-icon-button"
				dataAt="back-button-general"
			>
				<Icon type={ICON_ARROW_LEFT} data-testid="sidebar-header__back-icon-button" />
			</BackButtonIconBg>
		);
	}
};

LeftSideIcon.defaultProps = {
	type: LeftSideIconType.NONE
};

LeftSideIcon.propTypes = {
	type: PropTypes.oneOf(Object.values(LeftSideIconType)),
	onClick: PropTypes.func.isRequired
};

const SidebarHeader = ({
	children,
	className = '',
	leftSideIcon,
	onLeftSideButtonClick,
	Icon: SomeIcon,
	sticky,
	transparent,
	transparentClassName,
	opaqueClassName,
	isNew = false
}) => {
	const closeCurrentContext = useCloseCurrentContext();
	const handleCloseSidebar = event => {
		if (event) event.stopPropagation();
		if (onLeftSideButtonClick) {
			return onLeftSideButtonClick();
		}
		closeCurrentContext();
	};

	const classNames = [className || '', 'page-head', transparent ? transparentClassName : opaqueClassName].join(' ');

	return (
		<StyledDefaultHeader className={classNames} sticky={sticky} transparent={transparent} isNew={isNew}>
			<div className="page-head__left-side">
				<LeftSideIcon type={leftSideIcon} onClick={handleCloseSidebar} />
				{SomeIcon && SomeIcon}
			</div>
			{children}
		</StyledDefaultHeader>
	);
};

SidebarHeader.propTypes = {
	transparent: PropTypes.bool,

	// Classnames when transparent is toggled true
	transparentClassName: PropTypes.string,

	// Classnames when transparent is toggled false
	opaqueClassName: PropTypes.string,

	leftSideIcon: PropTypes.oneOf(Object.values(LeftSideIconType)),
	onLeftSideButtonClick: PropTypes.func,

	Icon: PropTypes.node,
	sticky: PropTypes.bool
};

SidebarHeader.defaultProps = {
	transparent: false,
	transparentClassName: 'u-bg-trans',
	opaqueClassName: '',
	Icon: null,
	leftSideIcon: LeftSideIconType.NONE,
	onLeftSideButtonClick: null,
	sticky: true
};
export default SidebarHeader;
